/**
 * Shared state for the single-table carousel transition.
 *
 * Switching tables remounts the per-table subtree (the providers in
 * pages/Game.tsx are keyed by gameID, so each table's transient state resets —
 * that is deliberate and unchanged). RebindingTableView hides that remount
 * behind a horizontal slide. Two other places need to know a slide is running,
 * and neither is in that component's tree, so the flag lives here:
 *
 *   • pages/Game.tsx skips its 0.8s entrance fade — replaying a fade-from-black
 *     on the incoming table is what made a switch look like a page reload.
 *   • SlideContainer ignores further swipe gestures, so a flurry of swipes
 *     can't stack transitions on top of each other.
 *
 * A deadline (rather than a boolean) means a dropped `end` can never wedge the
 * app into a permanently "switching" state.
 */

import { noteTableSwitch } from 'utils/switchProfile';

// Slide duration. Fast enough to feel responsive, long enough to read as
// motion rather than a cut — the 200-300ms mobile-carousel band.
export const TABLE_SWITCH_MS = 260;

let switchingUntil = 0;

/** Mark a table switch as in progress for the next `durationMs`. */
export const beginTableSwitch = (durationMs: number = TABLE_SWITCH_MS): void => {
  switchingUntil = Date.now() + durationMs;
  // Every switch path funnels through here, so this is where the perf tooling
  // brackets the switch to attribute long frames to it (no-op unless the HUD
  // enabled profiling). Finger drags call this with a long hold duration — those
  // are excluded so a drag doesn't measure a 60s "switch".
  if (durationMs <= TABLE_SWITCH_MS) noteTableSwitch();
};

/** Clear the flag early (the slide finished before its deadline). */
export const endTableSwitch = (): void => {
  switchingUntil = 0;
};

export const isTableSwitching = (): boolean => Date.now() < switchingUntil;

/**
 * OS-level "reduce motion" preference. Read at switch time rather than cached,
 * so toggling it takes effect without a reload. Falls back to false where
 * matchMedia is unavailable (older webviews, jsdom).
 */
export const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
};

export type SlideDirection = 'left' | 'right';

/**
 * Which way the carousel travels, from the shared subscription order (the same
 * order the navigation strip lays its cards out in — since EN-10545 that is
 * newest-first, so index 0 is the LEFTMOST card):
 *   • target further RIGHT in the strip → the view slides LEFT (next table)
 *   • target further LEFT              → the view slides RIGHT (previous table)
 * An unknown table (not in the order yet — e.g. a just-subscribed SnG) is about
 * to be prepended at index 0, i.e. it joins at the LEFT end, so it defaults to
 * 'right'. This used to default to 'left' on the pre-EN-10545 premise that new
 * tables joined at the right (SQE scan, Marlon 8/24); the window is one tick
 * before the fallback prepend lands, so it only ever showed as a first-frame
 * slide in the wrong direction.
 */
export const slideDirection = (from: string, to: string, order: string[]): SlideDirection => {
  const fromIdx = order.indexOf(String(from));
  const toIdx = order.indexOf(String(to));
  if (fromIdx === -1 || toIdx === -1) return 'right';
  return toIdx > fromIdx ? 'left' : 'right';
};
